import {Component} from 'react';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {Translate, I18n} from 'react-redux-i18n';
import {withRouter} from 'react-router-dom';
import {Collapse} from '@material-ui/core';

import {Button} from 'front-onceforall-core';

import * as snackbarsActions from '../../../actions/snackbars';
import authenticationService from '../../../services/authentication/AuthenticationService';
import projectService from '../../../services/project/ProjectService';
import subscriptionService from '../../../services/subscription/SubscriptionService';
import ProjectDescriptionForm from '../../../components/project/ProjectDescriptionForm';
import ProjectRoleForm from '../../../components/project/ProjectRoleForm';
import {MANAGE_PROJECT_PERMISSION} from '../../../utils/constant';
import ProjectTypeForm from '../../../components/project/ProjectTypeForm';
import {captureError} from '../../../utils/log';

export class CreateProjectView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      submitted: false,
      currentStep: 0,
      projectType: null,
      selectedRole: null,
      standardProjectCredits: 0,
      nominativeProjectCredits: 0
    };

    this.handleProjectCreation = this.handleProjectCreation.bind(this);
    this.handleSelectProjectType = this.handleSelectProjectType.bind(this);
    this.handleChangeProjectType = this.handleChangeProjectType.bind(this);
    this.handleSelectRole = this.handleSelectRole.bind(this);
    this.handleChangeRole = this.handleChangeRole.bind(this);
  }

  componentDidMount() {
    this.getSettings();
    const roles = authenticationService.roles();
    const canCreateProject = roles.hasPermission(MANAGE_PROJECT_PERMISSION);
    if (!canCreateProject) {
      captureError('Access Denied redirection from CreateProjectView');
      this.props.history.push('/access-denied');
    }
  }

  getSettings() {
    subscriptionService.findSubscriptionByOrganisation(this.props.organisation.organisationId)
      .then(subscription => {
        this.setState({
          standardProjectCredits: subscription ? subscription.maximumNumberOfStandardProjects - subscription.numberOfStandardProjects : 0,
          nominativeProjectCredits: subscription ? subscription.maximumNumberOfNominativeProjects - subscription.numberOfNominativeProjects : 0
        });
      })
      .catch(error => {
        captureError('CreateProjectView.getSettings', error);
        this.setState({
          standardProjectCredits: 0,
          nominativeProjectCredits: 0
        });
      });
  }

  handleSelectProjectType(projectType) {
    this.setState({
      currentStep: 1,
      projectType
    });
  }

  handleChangeProjectType() {
    this.setState({
      currentStep: 0,
      selectedRole: null,
      projectType: null
    });
  }

  handleSelectRole(role) {
    this.setState({
      currentStep: 2,
      selectedRole: role
    });
  }

  handleChangeRole() {
    this.setState({
      currentStep: 1,
      selectedRole: null
    });
  }

  handleProjectCreation(project) {
    if (this.state.submitted) {
      return;
    }

    this.setState({submitted: true});

    const projectData = {
      ...project,
      role: this.state.selectedRole,
      ownerOrganisationId: this.props.organisation.organisationId,
      projectType: this.state.projectType
    };

    projectService.createProjectWithDefaultIntervention(projectData)
      .then(response => {
        this.props.actions.addSuccess(<Translate
          value="project.create.creationSuccess"
          name={project.name}
        />);
        const data = {
          project: response.project,
          interventionId: response.interventionId
        };
        projectService.isProjectCreated(response.project.projectId)
          .then(() => {
            this.props.goToNextStep(data);
          })
          .finally(() => {
            this.setState({submitted: false});
          });
      })
      .catch(() => {
        this.setState({submitted: false});
        this.props.actions.addError(<Translate value="error.generic"/>);
      });
  }

  render() {
    const projectDescriptionFormNotice = [];
    projectDescriptionFormNotice.push(<Translate
      key="notice"
      value="project.description.notice"
    />);
    projectDescriptionFormNotice.push(<Translate
      key="requiredNotice"
      value="project.description.requiredNotice"
    />);

    return (
      <div>
        <div className="card">
          <div className="flex-container space-between">
            <div className="flex-container">
              <h2 className="m-0">
                <Translate value="project.typeForm.title"/>
              </h2>
              {this.state.projectType &&
              <div className="chip information-primary ml-4">
                <Translate value={`project.type.${this.state.projectType}`}/>
              </div>
              }
            </div>
            {this.state.projectType &&
            <Button
              data-id="changeTypeButton"
              type="secondary"
              size="small"
              onClick={this.handleChangeProjectType}
            >
              {I18n.t('project.typeForm.change')}
            </Button>
            }
          </div>
          <Collapse in={this.state.currentStep === 0}>
            <ProjectTypeForm
              onSelectType={this.handleSelectProjectType}
              organisation={this.props.organisation}
              standardProjectCredits={this.state.standardProjectCredits}
              nominativeProjectCredits={this.state.nominativeProjectCredits}
            />
          </Collapse>
        </div>
        <div className="card">
          <div className="flex-container space-between">
            <div className="flex-container">
              <h2 className="m-0"><Translate value="project.roleForm.title"/></h2>
              {this.state.selectedRole &&
              <div className="chip information-primary ml-4">
                <Translate value={`intervention.role.${this.state.selectedRole}`}/>
              </div>
              }
            </div>
            {this.state.selectedRole &&
              <Button
                data-id="changeRoleButton"
                type="secondary"
                size="small"
                onClick={this.handleChangeRole}
              >
                {I18n.t('project.roleForm.change')}
              </Button>
            }
          </div>
          <Collapse in={this.state.currentStep === 1}>
            <ProjectRoleForm
              organisationName={this.props.organisation.fullName}
              onSelectRole={this.handleSelectRole}
            />
          </Collapse>
        </div>
        <div className="card">
          <h2 className="m-0"><Translate value="project.description.title"/></h2>
          <Collapse in={this.state.currentStep === 2}>
            <ProjectDescriptionForm
              onValidate={this.handleProjectCreation}
              notice={projectDescriptionFormNotice}
              submitted={this.state.submitted}
            />
          </Collapse>
        </div>
      </div>
    );
  }
}

CreateProjectView.propTypes = {
  organisation: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  goToNextStep: PropTypes.func.isRequired
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({...snackbarsActions}, dispatch)
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CreateProjectView));
